import React, { useEffect, useState, useMemo } from "react";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { stripLeadingZeros, parseDotNetDate, formatDotDate, extractList } from "./appraisalHelper";

interface Props {
  show: boolean;
  onHide: () => void;
  appraisalDetail: any;
}

const safeString = (val?: any): string => {
  if (val === undefined || val === null) return "";
  const s = String(val).trim();
  return s;
};

const cleanAbsence = (val?: any): string => {
  if (val === undefined || val === null) return "";
  const s = String(val).trim();
  if (s.toLowerCase() === "period of absence from office during the year") {
    return "";
  }
  return s;
};

const toDisplayDate = (value?: string | null): string => {
  if (!value) return "";
  const s = String(value).trim();
  if (!s || s === "-") return "";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) return s;
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s.replace(/-/g, ".");
  const parsed = parseDotNetDate(s);
  if (parsed && parsed !== "-") return parsed.replace(/-/g, ".");
  return s;
};

const AppraisalDocumentInfoModal: React.FC<Props> = ({ show, onHide, appraisalDetail }) => {
  const [detailData, setDetailData] = useState<any>(appraisalDetail || {});
  const [loading, setLoading] = useState(false);

  const appraisalId =
    appraisalDetail?.appraisalId ||
    appraisalDetail?.AppraisalID ||
    appraisalDetail?.raw?.AppraisalID ||
    "";

  const appraiseeId = stripLeadingZeros(
    appraisalDetail?.appraiseeId ||
      appraisalDetail?.empId ||
      appraisalDetail?.AppraiseeID ||
      appraisalDetail?.employeeNumber ||
      appraisalDetail?.EmployeeNumber ||
      appraisalDetail?.raw?.AppraiseeID ||
      appraisalDetail?.raw?.EmployeeNumber ||
      appraisalDetail?.raw?.EmployeeID ||
      ""
  );

  useEffect(() => {
    if (show && appraisalId) {
      setLoading(true);
      const payload: any = { appraisalId };
      if (appraiseeId) {
        payload.appraiseeId = appraiseeId;
      }
      Api.post(API_URLS.APPRAISAL.DOCUMENT_DETAILS, payload)
        .then((response: any) => {
          const res = response?.data;
          if (res?.success || response?.status === 200) {
            const raw =
              (Array.isArray(res?.data?.data?.data) ? res.data.data.data[0] : null) ||
              (Array.isArray(res?.data?.data) ? res.data.data[0] : null) ||
              (Array.isArray(res?.data) ? res.data[0] : null) ||
              (Array.isArray(res) ? res[0] : null) ||
              extractList(res)[0] ||
              extractList(response)[0] ||
              (res?.data?.data && typeof res.data.data === "object" ? res.data.data : null) ||
              (res?.data && typeof res.data === "object" ? res.data : null) ||
              {};
            setDetailData((prev: any) => ({ ...prev, ...raw }));
          }
        })
        .catch((err) => {
          console.error("Error fetching info details:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [show, appraisalId, appraiseeId]);

  const rawData = detailData?.raw || detailData || {};

  const fields = useMemo(
    () => [
      {
        label: "Employee Number",
        value: safeString(
          rawData?.EmployeeNumber ??
            detailData?.employeeNumber ??
            detailData?.EmployeeNumber ??
            detailData?.empId ??
            ""
        ),
      },
      {
        label: "Designation",
        value: safeString(
          rawData?.Designation ?? detailData?.designation ?? detailData?.Designation ?? ""
        ),
      },
      {
        label: "Department",
        value: safeString(
          rawData?.Department ?? detailData?.department ?? detailData?.Department ?? ""
        ),
      },
      {
        label: "Payscale",
        value: safeString(
          rawData?.Payscale ??
            rawData?.PayScale ??
            detailData?.payscale ??
            detailData?.Payscale ??
            ""
        ),
      },
      {
        label: "Date of Birth",
        value: toDisplayDate(
          rawData?.DateOfBirth ??
            rawData?.BirthDate ??
            detailData?.dateOfBirth ??
            detailData?.DateOfBirth
        ),
      },
      {
        label: "Date of Appointment to Current Grade",
        value: toDisplayDate(
          rawData?.DateOfAppointmentToCurrentGrade ??
            rawData?.DateOfAppointment ??
            detailData?.dateOfAppointment ??
            detailData?.DateOfAppointmentToCurrentGrade
        ),
      },
      {
        label: "Date of Joining",
        value: toDisplayDate(
          rawData?.DateOfJoining ??
            rawData?.JoiningDate ??
            detailData?.dateOfJoining ??
            detailData?.DateOfJoining
        ),
      },
      {
        label: "Educational Qualifications",
        value: safeString(
          rawData?.EducationQualifications ??
            rawData?.EducationalQualifications ??
            detailData?.educationQualifications ??
            detailData?.EducationQualifications ??
            ""
        ),
      },
      {
        label: "Qualifications acquired during this assessment year",
        value: safeString(
          rawData?.QualificationsAcquiredYear ??
            rawData?.QualificationsAcquiredDuringYear ??
            detailData?.qualificationsAcquiredYear ??
            detailData?.QualificationsAcquiredYear ??
            ""
        ),
      },
      {
        label: "Period of Absence from office during the year",
        value: cleanAbsence(
          rawData?.AbsencePeriodInYear ??
            rawData?.AbsencePeriod ??
            detailData?.absencePeriod ??
            detailData?.AbsencePeriodInYear
        ),
      },
      {
        label: "Earned Leave",
        value: safeString(
          rawData?.EarnedLeave ?? detailData?.earnedLeave ?? detailData?.EarnedLeave ?? ""
        ),
      },
      {
        label: "Medical Leave",
        value: safeString(
          rawData?.MedicalLeave ?? detailData?.medicalLeave ?? detailData?.MedicalLeave ?? ""
        ),
      },
      {
        label: "Other Leaves",
        value: safeString(
          rawData?.OtherLeaves ?? detailData?.otherLeaves ?? detailData?.OtherLeaves ?? ""
        ),
      },
      {
        label: "Whether Medical check up done",
        value: safeString(
          rawData?.MedicalCheckupDone ??
            rawData?.WhetherMedicalCheckupDone ??
            detailData?.medicalCheckupDone ??
            detailData?.MedicalCheckupDone ??
            ""
        ),
      },
      {
        label: "Medical check up date",
        value: toDisplayDate(
          rawData?.MedicalCheckupDate ??
            detailData?.medicalCheckupDate ??
            detailData?.MedicalCheckupDate
        ),
      },
      {
        label: "Annual Property Return (APR) for current year",
        value: safeString(
          rawData?.AnnualPropertyReturnCurrentYear ??
            rawData?.AnnualPropertyReturn ??
            rawData?.APRCurrentYear ??
            rawData?.AprCurrentYear ??
            rawData?.APR ??
            rawData?.Apr ??
            detailData?.annualPropertyReturnCurrentYear ??
            detailData?.aprCurrentYear ??
            ""
        ),
      },
      {
        label: "Annual Property Return (APR) Submission Date",
        value: toDisplayDate(
          rawData?.AnnualPropertyReturnSubmissionDate ??
            rawData?.APRSubmissionDate ??
            rawData?.AprSubmissionDate ??
            rawData?.APRDate ??
            rawData?.AprDate ??
            rawData?.AnnualPropertyReturnDate ??
            detailData?.annualPropertyReturnSubmissionDate ??
            detailData?.aprSubmissionDate
        ),
      },
    ],
    [rawData, detailData]
  );

  if (!show) return null;

  return (
    <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content rounded-4 border-0 shadow">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-bold text-dark d-flex align-items-center">
              <i className="ti ti-info-circle text-primary me-2 fs-20"></i>
              Additional Data for Appraisee
            </h5>
            <button type="button" className="btn-close" onClick={onHide}></button>
          </div>
          <div className="modal-body p-4">
            {loading ? (
              <div className="text-center py-4">
                <i className="ti ti-loader fa-spin fs-24 text-primary d-block mb-2"></i>
                <span>Loading additional details...</span>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <tbody>
                    {fields.map((item, idx) => (
                      <tr key={idx}>
                        <td className="bg-light fw-medium text-secondary fs-13" style={{ width: "45%" }}>
                          {item.label}
                        </td>
                        <td className="fw-semibold text-dark fs-13">
                          {item.value || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer border-0 pt-0">
            <button type="button" className="btn btn-secondary btn-sm px-4 rounded-2" onClick={onHide}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppraisalDocumentInfoModal;
